// ─────────────────────────────────────────────────────────────────────────────
// Mp4StatusPanel.jsx
// Drop this component into Home.jsx and render it inside the MP4 panel,
// replacing the old <JobHistoryPanel> section.
//
// Features:
//   • Auto-refresh every 4s (jobs status + live FFmpeg stats)
//   • Per-platform live stats (fps, bitrate, elapsed) when job is streaming
//   • Stop button per job (calls POST /api/stream-status/file-jobs/:id/stop)
//   • Optimistic delete that removes the job row instantly
//   • No manual refresh button needed
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";

import AccessTimeIcon    from "@mui/icons-material/AccessTime";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon       from "@mui/icons-material/History";
import MovieIcon         from "@mui/icons-material/Movie";
import RefreshIcon       from "@mui/icons-material/Refresh";
import SpeedIcon         from "@mui/icons-material/Speed";
import StopCircleIcon    from "@mui/icons-material/StopCircle";

const COLORS = {
  panelSoft:   "rgba(255, 255, 255, 0.035)",
  border:      "rgba(255, 255, 255, 0.09)",
  text:        "#F7F7F8",
  muted:       "#8D96A7",
  cyan:        "#11C5D9",
  green:       "#12B981",
  amber:       "#F4B740",
  red:         "#F05252",
};

const formatBytes = (b) => {
  if (!b) return "–";
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(0)} MB`;
  return `${(b / 1e3).toFixed(0)} KB`;
};

const statusColor = (s) => {
  if (s === "live" || s === "running")          return COLORS.green;
  if (s === "done" || s === "completed")        return COLORS.cyan;
  if (s === "error" || s === "failed")          return COLORS.red;
  if (s === "cancelled")                        return COLORS.muted;
  return COLORS.amber; // scheduled
};

const statusLabel = (s) => ({
  scheduled: "Scheduled", live: "Live", running: "Streaming",
  done: "Done", completed: "Done", error: "Error",
  failed: "Error", cancelled: "Cancelled"
}[s] || s || "Scheduled");

// ── LiveStats bar shown when a job is actively streaming ─────────────────────
function LiveStatsBar({ stats }) {
  if (!stats || !Object.keys(stats).length) return null;

  return (
    <Stack spacing={0.5} mt={1}>
      {Object.entries(stats).map(([platform, s]) => (
        <Box key={platform} sx={{
          p: "6px 10px",
          bgcolor: "rgba(18,185,129,0.06)",
          border: "1px solid rgba(18,185,129,0.2)",
          borderRadius: "6px"
        }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography sx={{ color: COLORS.green, fontSize: 11, fontWeight: 800, minWidth: 90 }}>
              {platform}
            </Typography>
            {s.fps > 0 && (
              <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
                <SpeedIcon sx={{ fontSize: 11, verticalAlign: "middle", mr: 0.3 }} />
                {s.fps.toFixed(0)} fps
              </Typography>
            )}
            {s.bitrate > 0 && (
              <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
                {s.bitrate.toFixed(0)} kbps
              </Typography>
            )}
            {s.elapsed && (
              <Typography sx={{ color: COLORS.muted, fontSize: 11, fontFamily: "monospace" }}>
                {s.elapsed}
              </Typography>
            )}
            {s.frame > 0 && (
              <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
                f:{s.frame}
              </Typography>
            )}
            {/* Small live indicator dot */}
            <Box sx={{
              width: 6, height: 6, borderRadius: "50%",
              bgcolor: COLORS.green,
              animation: "pulse 1.4s infinite",
              ml: "auto !important"
            }} />
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Mp4StatusPanel({ axiosConfig, onDeleteJob }) {
  const [jobs,       setJobs]       = useState([]);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [lastUpdate, setLastUpdate]  = useState(null);
  const timerRef = useRef(null);

  // ── Fetch enriched job list (includes liveStats) ──────────────────────────
  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get("/api/stream-status/file-jobs", axiosConfig);
      const incoming = res.data?.jobs || [];

      setJobs((prev) => {
        // Keep optimistically-deleted rows out
        const hiddenIds = new Set(
          prev.filter((j) => j._optimisticDelete).map((j) => j.id)
        );
        return incoming.filter((j) => !hiddenIds.has(j.id));
      });

      setLastUpdate(new Date());
    } catch {}
  }, [axiosConfig]);

  // ── Auto-refresh: 4s when any job is live, 10s otherwise ─────────────────
  useEffect(() => {
    const hasLive = jobs.some((j) => j.status === "live" || j.status === "running");
    const interval = hasLive ? 4000 : 10000;

    clearInterval(timerRef.current);
    timerRef.current = setInterval(fetchJobs, interval);
    return () => clearInterval(timerRef.current);
  }, [fetchJobs, jobs]);

  // Initial fetch
  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // ── Stop a running job ────────────────────────────────────────────────────
  const stopJob = async (job) => {
    try {
      await axios.post(
        `/api/stream-status/file-jobs/${encodeURIComponent(job.id)}/stop`,
        {},
        axiosConfig
      );
      // Update local status immediately
      setJobs((prev) =>
        prev.map((j) => j.id === job.id ? { ...j, status: "cancelled" } : j)
      );
    } catch (err) {
      console.error("Could not stop job:", err);
    }
  };

  // ── Delete (cancel + optionally remove file) ──────────────────────────────
  const handleDelete = (job) => {
    // Optimistic: mark as deleting
    setDeletingIds((prev) => new Set([...prev, job.id]));
    // Delegate to parent (which shows the confirm dialog)
    onDeleteJob(job, () => {
      // On error callback: undo optimistic removal
      setDeletingIds((prev) => { const n = new Set(prev); n.delete(job.id); return n; });
    });
  };

  const visible = jobs.filter((j) => !deletingIds.has(j.id));

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} mb={1.25}>
        <HistoryIcon sx={{ fontSize: 15, color: COLORS.muted }} />
        <Typography sx={{
          color: COLORS.muted, fontSize: 12, fontWeight: 800,
          letterSpacing: "0.05em", textTransform: "uppercase", flex: 1
        }}>
          Stream history
        </Typography>
        <Chip
          label={visible.length} size="small"
          sx={{ height: 17, fontSize: 10, bgcolor: "rgba(255,255,255,0.06)", color: COLORS.muted }}
        />
        {lastUpdate && (
          <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: 10, fontFamily: "monospace" }}>
            {lastUpdate.toLocaleTimeString()}
          </Typography>
        )}
        <Tooltip title="Refresh now">
          <IconButton size="small" onClick={fetchJobs} sx={{ color: COLORS.muted }}>
            <RefreshIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Empty state */}
      {!visible.length && (
        <Stack alignItems="center" py={2.5} spacing={0.75}>
          <HistoryIcon sx={{ fontSize: 28, color: "rgba(255,255,255,0.07)" }} />
          <Typography sx={{ color: COLORS.muted, fontSize: 13 }}>No streams yet</Typography>
        </Stack>
      )}

      {/* Job cards */}
      <Stack spacing={1}>
        {visible.map((job) => {
          const color   = statusColor(job.status);
          const isLive  = job.status === "live" || job.status === "running";

          return (
            <Box key={job.id} sx={{
              p: 1.5, borderRadius: "8px",
              border: `1px solid ${isLive ? "rgba(18,185,129,0.35)" : COLORS.border}`,
              borderLeft: `3px solid ${color}`,
              bgcolor: isLive ? "rgba(18,185,129,0.04)" : COLORS.panelSoft,
              transition: "all 0.2s"
            }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ sm: "flex-start" }}
                justifyContent="space-between"
              >
                {/* Left: file info */}
                <Box flex={1} minWidth={0}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <MovieIcon sx={{ fontSize: 14, color: COLORS.amber, flexShrink: 0 }} />
                    <Typography noWrap sx={{ color: COLORS.text, fontWeight: 850, fontSize: 13 }}>
                      {job.originalName || job.fileName || "Scheduled MP4"}
                    </Typography>
                    {isLive && (
                      <Box sx={{
                        width: 7, height: 7, borderRadius: "50%", bgcolor: COLORS.green,
                        animation: "pulse 1.4s infinite", flexShrink: 0
                      }} />
                    )}
                  </Stack>

                  {/* Status row */}
                  <Stack direction="row" spacing={1.25} mt={0.5} flexWrap="wrap" alignItems="center">
                    <Chip
                      label={statusLabel(job.status)} size="small"
                      sx={{
                        height: 18, fontSize: 10, fontWeight: 900, borderRadius: "4px",
                        bgcolor: `${color}22`, color,
                        border: `1px solid ${color}44`
                      }}
                    />
                    <Typography sx={{ color: COLORS.muted, fontSize: 11, display: "flex", alignItems: "center", gap: 0.4 }}>
                      <AccessTimeIcon sx={{ fontSize: 11 }} />
                      {job.scheduledAt ? new Date(job.scheduledAt).toLocaleString() : "No time"}
                    </Typography>
                    {job.fileSize && (
                      <Typography sx={{ color: COLORS.muted, fontSize: 11 }}>
                        {formatBytes(job.fileSize)}
                      </Typography>
                    )}
                  </Stack>

                  {/* Destinations */}
                  {job.destinations?.length > 0 && (
                    <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                      {job.destinations.map((d) => (
                        <Chip key={d.platform} label={d.platform} size="small"
                          sx={{
                            height: 16, fontSize: 10,
                            bgcolor: isLive ? "rgba(18,185,129,0.12)" : "rgba(17,197,217,0.08)",
                            color: isLive ? COLORS.green : COLORS.cyan,
                            border: `1px solid ${isLive ? "rgba(18,185,129,0.25)" : "rgba(17,197,217,0.2)"}`,
                            borderRadius: "4px"
                          }}
                        />
                      ))}
                    </Stack>
                  )}

                  {/* Live FFmpeg stats */}
                  {isLive && job.liveStats && <LiveStatsBar stats={job.liveStats} />}

                  {/* Progress bar for live jobs */}
                  {isLive && (
                    <LinearProgress
                      sx={{
                        mt: 1, height: 3, borderRadius: 2,
                        bgcolor: "rgba(255,255,255,0.07)",
                        "& .MuiLinearProgress-bar": { bgcolor: COLORS.green }
                      }}
                    />
                  )}
                </Box>

                {/* Right: action buttons */}
                <Stack direction="row" spacing={0.75} flexShrink={0} mt={{ xs: 0.5, sm: 0 }}>
                  {/* Stop button — only for live/running jobs */}
                  {isLive && (
                    <Tooltip title="Stop stream">
                      <IconButton
                        onClick={() => stopJob(job)}
                        sx={{
                          color: COLORS.amber,
                          border: `1px solid rgba(244,183,64,0.3)`,
                          borderRadius: "8px"
                        }}
                      >
                        <StopCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* Removes the LOG ENTRY. The uploaded video is deleted from
                      the library instead — two separate buttons on purpose. */}
                  <Tooltip title={isLive ? "Stop & remove from log" : "Remove from log (keeps the video)"}>
                    <IconButton
                      onClick={() => handleDelete(job)}
                      sx={{
                        color: COLORS.red,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: "8px"
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
